import { useContext, useRef, useCallback } from 'react';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { companyName, flavor, version } from 'src/settings';
import {
  Box,
  Drawer,
  alpha,
  styled,
  Divider,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';

import SidebarMenu from './SidebarMenu';
import Scrollbar from 'src/components/Scrollbar';
import { ChevronLeft } from '@mui/icons-material';
import SidebarMenuShort from './SidebarMenuShort';
import { FormattedMessage } from 'react-intl';

const SidebarWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fixedExpanded'
})<{ fixedExpanded: boolean }>(
  ({ theme, fixedExpanded }) => `
        width: ${fixedExpanded ? theme.sidebar.width : '80px'};
        min-width: ${fixedExpanded ? theme.sidebar.width : '80px'};
        color: ${theme.sidebar.textColor};
        position: relative;
        z-index: 7;
        height: 100%;
        padding-bottom: 68px;
        transition: width 0.3s ease, min-width 0.3s ease;
`
);

function Sidebar() {
  const {
    sidebarToggle,
    toggleSidebar,
    fixedExpanded,
    setFixedExpanded } = useContext(SidebarContext);
  const closeSidebar = () => toggleSidebar();
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      sessionStorage.setItem('sidebar-scroll', String(scrollRef.current.scrollTop));
    }
  }, []);

  const restoreScroll = useCallback(() => {
    const saved = sessionStorage.getItem('sidebar-scroll');
    if (!saved || !scrollRef.current) return;
    const target = Number(saved);
    // Wait for Collapse animations to finish (MUI default is 300ms)
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = target;
      }
    }, 400);
  }, []);


  return (
    <>
      <SidebarWrapper
        fixedExpanded={fixedExpanded}
        sx={{
          display: {
            xs: "none",
            lg: "inline-block",
          },
          position: "fixed",
          left: 0,
          top: 0,
          background: theme.sidebar.background,
          boxShadow:
            theme.palette.mode === "dark" ? theme.sidebar.boxShadow : "none",
        }}
      >
        <Scrollbar ref={scrollRef} onScroll={handleScroll}>
          <Box
            mt={fixedExpanded ? 3 : 2}
            display="flex"
            justifyContent="center"
            position="relative"
            alignItems="center"
            minHeight="64px"
          >
            {fixedExpanded && (
              <Tooltip title={<FormattedMessage id="retractMenu" />} arrow>
              <IconButton
                component="div"
                size="small"
                sx={{
                  position: 'absolute',
                  left: 18,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: theme.colors.alpha.trueWhite[70],
                  '&:hover': {
                    color: theme.colors.alpha.trueWhite[100],
                    backgroundColor: alpha(theme.colors.alpha.trueWhite[100], 0.1)
                  },
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setFixedExpanded(false)}
                disableRipple={false}
              >
                <ChevronLeft />
              </IconButton>
              </Tooltip>
            )}
            <Box
              component="img"
              src={"/" + flavor + "/shield.png"}
              sx={{
                width: fixedExpanded ? 100 : 50,
                transition: 'width 0.3s ease',
                mx: 'auto',
                display: 'block'
              }}
              alt="Logo"
            />
          </Box>
          {fixedExpanded ? <SidebarMenu onReady={restoreScroll} /> : <SidebarMenuShort />}
        </Scrollbar>
        {fixedExpanded && (
          <>
            <Divider
              sx={{
                background: theme.colors.alpha.trueWhite[10],
              }}
            />
            <Box p={2} textAlign="center" color={theme.colors.primary.dark}>
              {companyName}
              <br />
              {version}
            </Box>
          </>
        )}
      </SidebarWrapper>
      <Drawer
        sx={{
          boxShadow: `${theme.sidebar.boxShadow}`,
          zIndex: theme.zIndex.drawer + 1,
          '& .MuiDrawer-paper': {
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none'
          }
        }}
        anchor={theme.direction === "rtl" ? "right" : "left"}
        open={sidebarToggle}
        onClose={closeSidebar}
        variant="temporary"
        elevation={9}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }
          }
        }}
      >
          <SidebarWrapper
          fixedExpanded
          sx={{
            width: theme.sidebar.width,
            minWidth: theme.sidebar.width,
            background: theme.sidebar.background,
          }}
        >
          <Scrollbar
            sx={{
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}
          >
            <Box mt={3} display="flex" justifyContent="center">
              <Box
                mx={2}
                component="img"
                src={"/" + flavor + "/shield.png"}
                sx={{
                  width: 100,
                }}
              />
            </Box>
            <Divider
              sx={{
                mt: theme.spacing(3),
                mx: theme.spacing(2),
                background: theme.colors.alpha.trueWhite[10],
              }}
            />
            <SidebarMenu />
          </Scrollbar>
          <Divider
            sx={{
              background: theme.colors.alpha.trueWhite[10],
            }}
          />
          <Box p={2}
            textAlign="center"
            color={theme.colors.primary.dark}>
            <b>{companyName}</b>
            <br />
            {version}
          </Box>
        </SidebarWrapper>
      </Drawer>
    </>
  );
}

export default Sidebar;
